<template>
  <main class="home-page">
    <inventory-search
      v-model:query="query"
      class="home-search"
      :search-results="searchResults"
      @search-submit="navigateToFirstSearchMatch"
      @search-result="navigateToSearchResult"
    ></inventory-search>

    <header class="home-hero">
      <div>
        <h1>SUBANGSTROM</h1>
        <p>Inventory Management System</p>
      </div>
    </header>

    <section class="home-grid" aria-label="Primary inventory workflows">
      <home-item @click="changePage('my-profile')" image="my-profile">
        My Profile
      </home-item>
      <home-item @click="changePage('manage-products')" image="manage-products">
        Manage Products
      </home-item>
      <home-item
        v-if="appStore.state.activeUser?.role == 'lab-admin'"
        @click="changePage('transport-racks')"
        image="transport-racks"
      >
        Transport Racks
      </home-item>
      <home-item
        v-if="appStore.state.activeUser?.role == 'lab-admin'"
        @click="changePage('project-manager')"
        image="project-manager"
      >
        Project Manager
      </home-item>
      <home-item @click="changePage('archives')" image="grid-explorer">
        Grid Explorer
      </home-item>
      <home-item
        v-if="appStore.state.activeUser?.role == 'lab-admin'"
        @click="changePage('shared-information')"
        image="shared-information"
      >
        Shared Products
      </home-item>
      <home-item
        v-if="appStore.state.activeUser?.role == 'lab-admin'"
        @click="changePage('my-group')"
        image="group-admin"
      >
        Group Admin Page
      </home-item>
      <home-item @click="changePage('team-members')" image="team-members">
        Team Members
      </home-item>
      <home-item
        v-if="appStore.state.activeUser?.role == 'lab-admin'"
        @click="changePage('analytics')"
        image="analytics"
      >
        Analytics
      </home-item>
      <home-item
        v-if="appStore.state.activeUser?.role == 'lab-admin'"
        @click="changePage('data-settings')"
        image="data-settings"
      >
        Data Settings
      </home-item>
      <home-item @click="changePage('customer-support')" image="customer-support">
        Customer Support
      </home-item>
    </section>
  </main>
</template>

<script>
import HomeItem from "../../HomeItem.vue";
import InventorySearch from "../../ui/InventorySearch.vue";
import inventoryUiActions from "../../../mixins/inventoryUiActions";
import { useInventoryStore } from "../../../stores/inventory";

export default {
  mixins: [inventoryUiActions],
  props: ["changePage"],
  components: {
    HomeItem,
    InventorySearch,
  },
  data() {
    return {
      query: "",
      showFeedback: false,
      inventoryStore: useInventoryStore(),
    };
  },
  computed: {
    searchResults() {
      const needle = this.query.trim().toLowerCase();
      if (!needle) return [];

      const results = [];
      const push = (result) => {
        if (results.length < 10) results.push(result);
      };

      for (const puck of this.inventoryStore.pucksList) {
        if (results.length >= 10) break;
        if (!this.includesSearchTerm(needle, [
          puck.title,
          puck.name,
          puck.serialNo,
          puck.customId,
          puck.description,
          puck.projectId,
          puck.project,
        ])) continue;

        push({
          type: "puck",
          id: this.canonicalId(puck),
          puckId: this.canonicalId(puck),
          label: this.labelFor(puck, "Puck"),
          meta: `Puck${puck.customId || puck.serialNo ? ` ${puck.customId || puck.serialNo}` : ""}`,
          detail: this.pathForPuck(puck),
          color: this.transformPuckColor(puck.color || ""),
        });
      }

      for (const box of this.inventoryStore.boxesList) {
        if (results.length >= 10) break;
        if (!this.includesSearchTerm(needle, [
          box.title,
          box.name,
          box.customId,
          box.description,
          box.projectId,
          box.boxType,
        ])) continue;

        const puck = box.puckId ? this.inventoryStore.getPuck(box.puckId) : null;
        const wellNo = Number(box.wellNo) || 1;
        push({
          type: "box",
          id: this.canonicalId(box),
          boxId: this.canonicalId(box),
          puckId: this.canonicalId(puck),
          wellNo,
          label: this.labelFor(box, "Box"),
          meta: `Box${box.customId ? ` ${box.customId}` : ""}`,
          detail: `${this.pathForPuck(puck)} / ${this.labelFor(puck, "Puck")} / Well ${wellNo}`,
          color: this.transformBoxColor(box.color || ""),
        });
      }

      for (const grid of this.inventoryStore.gridsList) {
        if (results.length >= 10) break;
        if (!this.includesSearchTerm(needle, [
          grid.name,
          grid.title,
          grid.customId,
          grid.description,
          grid.gridCatalogType,
          grid.status,
        ])) continue;

        const box = grid.boxId ? this.inventoryStore.getBox(grid.boxId) : null;
        const puck = box?.puckId ? this.inventoryStore.getPuck(box.puckId) : null;
        const wellNo = Number(box?.wellNo) || 1;
        const gridPosition = Number(grid.position) || 1;
        push({
          type: "grid",
          id: this.canonicalId(grid),
          gridId: this.canonicalId(grid),
          boxId: this.canonicalId(box),
          puckId: this.canonicalId(puck),
          wellNo,
          gridPosition,
          label: this.labelFor(grid, "Grid"),
          meta: `Grid slot ${gridPosition}`,
          detail: `${this.pathForPuck(puck)} / ${this.labelFor(puck, "Puck")} / Well ${wellNo}`,
          color: "#d9e1eb",
        });
      }

      return results;
    },
  },
  async mounted() {
    await this.ensureInventorySnapshotLoaded();
  },
  methods: {
    canonicalId(value) {
      if (!value) return null;
      if (typeof value === "string") return value;
      return value.id || null;
    },
    includesSearchTerm(needle, values) {
      return values.some((value) => String(value || "").trim().toLowerCase().includes(needle));
    },
    labelFor(entity, fallback) {
      return entity?.title || entity?.name || entity?.customId || entity?.serialNo || fallback;
    },
    pathForPuck(puck) {
      const rack = puck?.rackId ? this.inventoryStore.getRack(puck.rackId) : null;
      const dewar = rack?.dewarId ? this.inventoryStore.getDewar(rack.dewarId) : null;
      return [this.labelFor(dewar, "Dewar"), this.labelFor(rack, "Rack")].filter(Boolean).join(" / ");
    },
    transformPuckColor(color) {
      if (!color) return "#0ea5e9";
      try {
        return this.appStore.state.transformPuckColor(color);
      } catch {
        return color;
      }
    },
    transformBoxColor(color) {
      if (!color) return "#facc15";
      try {
        return this.appStore.state.transformBoxColor(color);
      } catch {
        return color;
      }
    },
    navigateToFirstSearchMatch() {
      const match = this.searchResults[0];
      if (match) {
        this.navigateToSearchResult(match);
      }
    },
    navigateToSearchResult(result) {
      const specialNavigation = {
        puckId: result.puckId,
        boxId: result.boxId,
        gridId: result.gridId,
        well: result.wellNo,
        gridPosition: result.gridPosition,
      };
      this.$router.push({
        path: "/manage-products",
        query: { navigation: JSON.stringify(specialNavigation) },
      });
      this.query = "";
    },
  },
};
</script>

<style scoped>
.home-page {
  display: grid;
  align-content: start;
  gap: 16px;
  min-height: calc(100vh - 24px);
  padding: 0 18px 24px;
  background: #fff;
  color: rgb(5, 46, 77);
  font-family: Montserrat, Inter, system-ui, sans-serif;
}

.home-search {
  width: 100%;
}

.home-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 0 0 4px;
  border: 0;
  background: #fff;
  text-align: center;
}

.home-hero > div {
  flex: 1 1 auto;
}

.home-hero p,
.home-hero h1 {
  margin: 0;
}

.home-hero p {
  color: #172033;
  font-size: 24px;
  font-weight: 500;
}

.home-hero h1 {
  color: rgb(5, 46, 77);
  font-size: 36px;
  font-weight: 900;
  line-height: 1.15;
}

.home-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(128px, 250px));
  justify-content: center;
  justify-items: stretch;
  gap: 28px;
}

@media (max-width: 720px) {
  .home-hero {
    align-items: flex-start;
    flex-direction: column;
    text-align: left;
  }

  .home-hero h1 {
    font-size: 28px;
  }

  .home-hero p {
    font-size: 18px;
  }

  .home-grid {
    grid-template-columns: minmax(0, 250px);
    gap: 18px;
  }
}

@media (min-width: 768px) {
  .home-grid {
    grid-template-columns: repeat(3, minmax(150px, 250px));
  }
}

@media (min-width: 1180px) {
  .home-grid {
    grid-template-columns: repeat(4, minmax(170px, 250px));
  }
}
</style>
